import { StyleSheet } from 'aphrodite';
import { useEffect, useId, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
	CustomerAssistantAvatar,
	CustomerAssistantContent,
	CustomerAssistantConversationStarterCard,
	CustomerAssistantConversationStarters,
	CustomerAssistantDescription,
	CustomerAssistantTitle,
} from '../../../admin/containers/AccountCustomerAssistant/CustomerAssistantUI';
import { AIChatRole, AIConversationStarter } from '../../../extViewmodels/sdk/models/AI';
import {
	PromptInput,
	PromptInputBody,
	PromptInputFooter,
	PromptInputMessage,
	PromptInputSpeechButton,
	PromptInputSubmit,
	PromptInputTextarea,
	PromptInputTools,
} from '../../../web/components-v2/ai/PromptInput';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { brandPrimary, charmGray, shuffleGray, titles, white } from '../../../web/styles/colors';
import { shadowSm } from '../../../web/styles/shadows';
import { useIsCustomerAssistantEmbedMode } from '../../hooks/useIsCustomerAssistantEmbedMode';
import { usePublicAIConversationAgentTemplateQuery } from '../../queries/usePublicAIConversationAgentTemplateQuery';
import { usePublicAIConversationCreateMutation } from '../../queries/usePublicAIConversationCreateMutation';
import {
	getParentOriginFromSearch,
	LEV_ASSISTANT_CONVERSATION_UPDATED_MESSAGE,
	LEV_ASSISTANT_READY_MESSAGE,
	postMessageToParent,
} from '../../utils/embedMessaging';
import { CustomerAssistantLayout } from './CustomerAssistantLayout';

const CLIENT_IDENTIFIER_KEY = 'lev-public-ai-id';

export function CustomerAssistantStarterPage() {
	const [clientIdentifier] = useState(() => {
		let clientId = window.localStorage.getItem(CLIENT_IDENTIFIER_KEY);
		if (!clientId) {
			clientId = crypto.randomUUID();
			window.localStorage.setItem(CLIENT_IDENTIFIER_KEY, clientId);
		}
		return clientId;
	});
	const id = useId();
	const history = useHistory();
	const location = useLocation();
	const isEmbedMode = useIsCustomerAssistantEmbedMode();
	const parentOrigin = getParentOriginFromSearch(location.search);
	const { agentTemplateId } = useParams<{ agentTemplateId: string }>();
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const agentTemplateQuery = usePublicAIConversationAgentTemplateQuery({
		id: agentTemplateId,
		enabled: Boolean(agentTemplateId),
	});
	const createConversationMutation = usePublicAIConversationCreateMutation({
		onError: error => {
			console.error(error);
			toast.error('Error initiating chat');
		},
	});
	useEffect(() => {
		if (!isEmbedMode) {
			return;
		}

		postMessageToParent({
			type: LEV_ASSISTANT_READY_MESSAGE,
			targetOrigin: parentOrigin,
		});
	}, [isEmbedMode, parentOrigin]);

	const handleSubmit = async ({ text }: PromptInputMessage) => {
		if (!text) {
			return;
		}
		const conversation = await createConversationMutation.mutateAsync({
			agentTemplateId,
			clientIdentifier,
			timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		});

		if (isEmbedMode) {
			postMessageToParent({
				type: LEV_ASSISTANT_CONVERSATION_UPDATED_MESSAGE,
				payload: { conversationId: conversation.id },
				targetOrigin: parentOrigin,
			});
		}

		history.push({
			pathname: `/${agentTemplateId}/${conversation.id}`,
			search: location.search,
			state: {
				starterMessages: [
					{
						role: AIChatRole.User,
						text,
						id: null,
					},
				],
			},
		});
	};
	const handleStarterPicked = async (starter: AIConversationStarter) => {
		const conversation = await createConversationMutation.mutateAsync({
			agentTemplateId,
			clientIdentifier,
			timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		});

		if (isEmbedMode) {
			postMessageToParent({
				type: LEV_ASSISTANT_CONVERSATION_UPDATED_MESSAGE,
				payload: { conversationId: conversation.id },
				targetOrigin: parentOrigin,
			});
		}

		history.push({
			pathname: `/${agentTemplateId}/${conversation.id}`,
			search: location.search,
			state: { starterMessages: starter.chatMessages ?? [{ role: AIChatRole.User, text: starter.title, id: null }] },
		});
	};
	if (!agentTemplateQuery.isSuccess) {
		return (
			<CustomerAssistantLayout>
				<LoadingSpinner />
			</CustomerAssistantLayout>
		);
	}
	return (
		<CustomerAssistantLayout>
			<CustomerAssistantContent>
				{agentTemplateQuery.data.agentAvatarImage?.url ? (
					<CustomerAssistantAvatar imgSrc={agentTemplateQuery.data.agentAvatarImage.url} fallbackText='AI' />
				) : null}
				<CustomerAssistantTitle styles={[s.name]}>{agentTemplateQuery.data?.name}</CustomerAssistantTitle>
				<CustomerAssistantDescription styles={[s.description]}>
					{agentTemplateQuery.data?.description}
				</CustomerAssistantDescription>
				{agentTemplateQuery.data?.conversationStarters?.length ? (
					<CustomerAssistantConversationStarters styles={[s.starterGrid]}>
						{agentTemplateQuery.data.conversationStarters.map((starter, index) => (
							<CustomerAssistantConversationStarterCard
								key={`${id}-starter-${index}`}
								onClick={() => handleStarterPicked(starter)}
								disabled={createConversationMutation.isPending}
							>
								{starter.title}
							</CustomerAssistantConversationStarterCard>
						))}
					</CustomerAssistantConversationStarters>
				) : null}
				<PromptInput styles={[s.starterInput]} onSubmit={handleSubmit}>
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							{!isEmbedMode ? <PromptInputSpeechButton textareaRef={textareaRef} /> : null}
						</PromptInputTools>
						<PromptInputSubmit disabled={createConversationMutation.isPending} />
					</PromptInputFooter>
				</PromptInput>
			</CustomerAssistantContent>
		</CustomerAssistantLayout>
	);
}

const s = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
	},
	starterRoot: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: `32px 16px`,
		maxWidth: 608,
		width: '100%',
		margin: '0 auto',
		'@media (min-width: 768px)': {
			paddingTop: 48,
			paddingBottom: 48,
		},
	},
	sparklesIcon: {
		width: 20,
		height: 20,
		color: brandPrimary,
	},
	starterHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		color: brandPrimary,
		marginBottom: 16,
		fontSize: '0.875rem',
	},
	starterAskLev: {
		fontSize: '0.875rem',
		fontWeight: 'bold',
	},
	assistantThinkingAvatar: {
		width: 84,
		height: 84,
		marginBottom: 12,
		marginTop: 24,
	},
	name: {
		marginTop: 24,
	},
	description: {
		marginTop: 16,
	},
	starterGrid: {
		marginTop: 32,
	},
	starterCard: {
		boxSizing: 'border-box',
		flexBasis: 248,
		padding: `16px 24px`,
		border: `1px solid ${shuffleGray}`,
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		borderRadius: 12,
		backgroundColor: white,
		boxShadow: shadowSm,
		':hover': {
			backgroundColor: '#18181b0d',
		},
	},
	starterCardHeader: {
		fontSize: '0.875rem',
		color: titles,
	},
	starterCardSubtext: {
		fontSize: '0.75rem',
		color: charmGray,
	},
	loadingContainer: {
		display: `flex`,
		justifyContent: 'center',
	},
	starterInput: {
		marginTop: 32,
	},
});
